import { useState, useEffect, useRef } from 'react';

import { TMDB_API_BASE_URL, LANGUAGE } from '../constants';
import { COLLECTIONS_CONFIG } from '../constants/collections';

import type { MediaType, Genre, MediaRef, MediaPreview, LibraryItem, TMDBPaginatedResponse, LibraryCollection } from '../types';

import { DateInput } from '../components/DateInput';
import { Pagination } from '../components/Pagination';
import { MediaGrid } from '../components/MediaGrid';

import { toPreview } from '../utils/media';

import styles from './DiscoveryView.module.css';
import { CollectionIcon } from '../components/CollectionIcon';

const DATE_SEPARATOR_CHAR = '/';

interface DiscoveryViewProps {
    apiKey: string;
    activeTab: MediaType;
    // URL Params
    currentPage: number;
    sortBy: string;
    isDescending: boolean;
    minRating: number;
    hiddenCollections: Record<LibraryCollection, boolean>;
    startDate: string;
    endDate: string;
    activeGenres: string;
    updateParams: (newParams: Record<string, string | null>) => void;
    // Library
    library: LibraryItem[];
    updateCollection: (ref: MediaRef, target: LibraryCollection) => void;
    onOpenModal: (id: number, type: MediaType) => void;
}

export const DiscoveryView = ({
    apiKey, activeTab, currentPage, sortBy, isDescending, minRating,
    hiddenCollections, startDate, endDate, activeGenres,
    updateParams, library, updateCollection, onOpenModal
}: DiscoveryViewProps) => {

    // --- STATE ---
    const [items, setItems] = useState<MediaPreview[]>([]);
    const [loading, setLoading] = useState<boolean>(false);
    const [totalPages, setTotalPages] = useState<number>(1);
    const [genreList, setGenreList] = useState<{ movie: Genre[], tv: Genre[] }>({ movie: [], tv: [] });

    // Local Date State (for the 3-input fields)
    const [localStartDate, setLocalStartDate] = useState(startDate);
    const [localEndDate, setLocalEndDate] = useState(endDate);

    // Refs
    const sMonthRef = useRef<HTMLInputElement>(null);
    const sYearRef = useRef<HTMLInputElement>(null);
    const eMonthRef = useRef<HTMLInputElement>(null);
    const eYearRef = useRef<HTMLInputElement>(null);

    // --- EFFECTS ---
    // Keep local date state in sync if url changes
    useEffect(() => { setLocalStartDate(startDate); }, [startDate]);
    useEffect(() => { setLocalEndDate(endDate); }, [endDate]);

    // Fetch Genres
    useEffect(() => {
        const fetchAllGenres = async () => {
            try {
                const [movieRes, tvRes] = await Promise.all([
                    fetch(`${TMDB_API_BASE_URL}/genre/movie/list?api_key=${apiKey}&language=${LANGUAGE}`),
                    fetch(`${TMDB_API_BASE_URL}/genre/tv/list?api_key=${apiKey}&language=${LANGUAGE}`)
                ]);
                const [movieData, tvData] = await Promise.all([movieRes.json(), tvRes.json()]);
                setGenreList({ movie: movieData.genres, tv: tvData.genres });
            } catch (err) {
                console.error("Genre fetch failed", err);
            }
        };
        fetchAllGenres();
    }, [apiKey]);

    // Fetch Discovery Data
    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);
            const isMovie = activeTab === 'movie';
            const endpoint = isMovie ? 'discover/movie' : 'discover/tv';
            const startKey = isMovie ? 'primary_release_date.gte' : 'first_air_date.gte';
            const endKey = isMovie ? 'primary_release_date.lte' : 'first_air_date.lte';

            const url = `${TMDB_API_BASE_URL}/${endpoint}?api_key=${apiKey}&language=${LANGUAGE}&page=${currentPage}&vote_average.gte=${minRating}&${startKey}=${startDate}&${endKey}=${endDate}&with_genres=${activeGenres}&sort_by=${sortBy}.${isDescending ? 'desc' : 'asc'}`;

            try {
                const response = await fetch(url);
                const data: TMDBPaginatedResponse = await response.json();
                setTotalPages(data.total_pages > 500 ? 500 : data.total_pages);

                const normalizedData = data.results.map(item => toPreview(item, activeTab));

                setItems(normalizedData);
            } catch (error) {
                console.error("Error fetching TMDB data:", error);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [currentPage, activeTab, startDate, endDate, minRating, sortBy, isDescending, activeGenres, apiKey]);


    // --- COMPUTED / FILTERS ---
    const itemsToDisplay = items.filter(item => {
        const libraryEntry = library.find(l => l.id === item.id && l.media_type === item.media_type);

        // If it exists in library, check if that collection is marked 'hidden' (true)
        // If it's hidden, we return false to filter it out.
        return libraryEntry ? !hiddenCollections[libraryEntry.collection] : true;
    });

    const currentSelectedGenres = activeGenres ? activeGenres.split(',').map(Number) : [];
    // --- HANDLERS ---
    const handleVisibilityToggle = (collection: LibraryCollection) => {
        console.log('Hello there', collection);

        const paramKey = COLLECTIONS_CONFIG[collection].urlKey;

        const newValue = !hiddenCollections[collection];

        updateParams({
            [paramKey]: newValue.toString(),
            page: '1'
        });
    };

    const handleRatingChange = (val: number) => updateParams({ minR: val.toString(), page: '1' });
    const handleSortChange = (val: string) => updateParams({ sort: val, page: '1' });
    const handleSortDirToggle = () => updateParams({ dir: isDescending ? 'asc' : 'desc', page: '1' });
    const handlePageChange = (page: number) => updateParams({ page: page.toString() });

    const handleDatePartChange = (type: 'start' | 'end', part: 'y' | 'm' | 'd', value: string) => {
        const setter = type === 'start' ? setLocalStartDate : setLocalEndDate;
        const currentStr = type === 'start' ? localStartDate : localEndDate;
        const parts = currentStr.split('-');
        let [y, m, d] = parts;

        if (part === 'y') y = value;
        if (part === 'm') m = value;
        if (part === 'd') d = value;

        setter(`${y}-${m}-${d}`);
    };

    const handleDateBlur = (type: 'start' | 'end') => {
        const currentStr = type === 'start' ? localStartDate : localEndDate;
        const [y, m, d] = currentStr.split('-');
        const currentYear = new Date().getFullYear();

        const cleanY = (y || (type === 'start' ? '1900' : currentYear.toString())).padStart(4, '0');
        const cleanM = (m || '01').padStart(2, '0');
        const cleanD = (d || '01').padStart(2, '0');

        let yearNum = parseInt(cleanY);
        if (yearNum < 1870) yearNum = 1870;
        if (yearNum > currentYear) yearNum = currentYear;

        const finalDate = `${yearNum}-${cleanM}-${cleanD}`;

        if (type === 'start') setLocalStartDate(finalDate);
        else setLocalEndDate(finalDate);

        updateParams({ [type === 'start' ? 'sDate' : 'eDate']: finalDate, page: '1' });
    };

    return (
        <div className={styles.discoveryView}>
            <div className={styles.filtersBar}>
                <div className={styles.filtersInputsRow}>

                    {/* Visibility */}
                    <div className={styles.filterGroup}>
                        <label>Masquer les {activeTab === 'movie' ? 'films' : 'séries'}</label>
                        <div className={styles.visibilityTogglesWrapper}>
                            {Object.entries(COLLECTIONS_CONFIG).map(([key, config]) => {
                                const collection = key as LibraryCollection;
                                return (
                                    <button
                                        key={collection}
                                        onClick={() => handleVisibilityToggle(collection)}
                                        className={`${styles.visBtn} ${hiddenCollections[collection] ? styles.active : ''}`}
                                    >
                                        <CollectionIcon name={collection} />
                                        {config.label}
                                    </button>
                                );
                            })}
                        </div>
                    </div>

                    {/* Date filter */}
                    <div className={styles.filterGroup}>
                        <label>Période</label>
                        <div className={styles.dateInputsWrapper}>
                            <div className={styles.dateSubGroupAttached} onBlur={(e) => { if (!e.currentTarget.contains(e.relatedTarget as Node)) handleDateBlur('start'); }}>
                                <DateInput label="day" value={localStartDate.split('-')[2]} maxLength={2} nextRef={sMonthRef} onCommit={(v) => handleDatePartChange('start', 'd', v)} />
                                <span className={styles.dateSeparatorChar}>{DATE_SEPARATOR_CHAR}</span>
                                <DateInput ref={sMonthRef} label="month" value={localStartDate.split('-')[1]} maxLength={2} nextRef={sYearRef} onCommit={(v) => handleDatePartChange('start', 'm', v)} />
                                <span className={styles.dateSeparatorChar}>{DATE_SEPARATOR_CHAR}</span>
                                <DateInput ref={sYearRef} label="year" value={localStartDate.split('-')[0]} maxLength={4} onCommit={(v) => handleDatePartChange('start', 'y', v)} />
                            </div>
                            <span className={styles.dateSeparator}>au</span>
                            <div className={styles.dateSubGroupAttached} onBlur={(e) => { if (!e.currentTarget.contains(e.relatedTarget as Node)) handleDateBlur('end'); }}>
                                <DateInput label="day" value={localEndDate.split('-')[2]} maxLength={2} nextRef={eMonthRef} onCommit={(v) => handleDatePartChange('end', 'd', v)} />
                                <span className={styles.dateSeparatorChar}>{DATE_SEPARATOR_CHAR}</span>
                                <DateInput ref={eMonthRef} label="month" value={localEndDate.split('-')[1]} maxLength={2} nextRef={eYearRef} onCommit={(v) => handleDatePartChange('end', 'm', v)} />
                                <span className={styles.dateSeparatorChar}>{DATE_SEPARATOR_CHAR}</span>
                                <DateInput ref={eYearRef} label="year" value={localEndDate.split('-')[0]} maxLength={4} onCommit={(v) => handleDatePartChange('end', 'y', v)} />
                            </div>
                        </div>
                    </div>

                    {/* Rating filter */}
                    <div className={styles.filterGroup}>
                        <label>Note Mini</label>
                        <div className={styles.ratingInputWrapper}>
                            <input
                                type="number"
                                step="0.1"
                                max="10"
                                min="0"
                                value={minRating}
                                onChange={(e) => handleRatingChange(Number(e.target.value))}
                                onFocus={(e) => e.target.select()}
                            />
                            <span className={styles.starSuffix}>★</span>
                        </div>
                    </div>

                    {/* Sorting filter */}
                    <div className={styles.filterGroup}>
                        <label>Trier par</label>
                        <div className={styles.sortControls}>
                            <select className={styles.sortSelect} value={sortBy} onChange={(e) => handleSortChange(e.target.value)}>
                                <option value="popularity">Popularité</option>
                                <option value="vote_average">Note</option>
                                <option value="primary_release_date">Sortie</option>
                            </select>
                            <button className={styles.sortDirectionBtn} onClick={handleSortDirToggle}>
                                {isDescending ? "↓" : "↑"}
                            </button>
                        </div>
                    </div>

                    {/* Genres filter */}
                    <div className={`${styles.filterGroup} ${styles.fullWidth}`}>
                        <div className={styles.genresControls}>
                            <label>Genres</label>
                            {currentSelectedGenres.length > 0 && (
                                <button className={styles.clearGenresBtn} onClick={() => updateParams({ genres: '' })}>Réinitialiser</button>
                            )}
                        </div>
                        <div className={styles.genresContainer}>
                            {genreList[activeTab]?.map((genre) => {
                                const isSelected = currentSelectedGenres.includes(genre.id);
                                return (
                                    <button
                                        key={genre.id}
                                        className={`${styles.genreChip} ${isSelected ? styles.active : ''}`}
                                        onClick={() => {
                                            const newList = isSelected ? currentSelectedGenres.filter(id => id !== genre.id) : [...currentSelectedGenres, genre.id];
                                            updateParams({ genres: newList.join(','), page: '1' });
                                        }}
                                    >
                                        {genre.name}
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                </div>
            </div>

            <div id="results-top" />
            {itemsToDisplay.length > 0 && (
                <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={handlePageChange} loading={loading} />
            )}

            <MediaGrid
                items={itemsToDisplay}
                loading={loading}
                activeTab={activeTab}
                library={library}
                updateCollection={updateCollection}
                onOpenModal={onOpenModal}
            />

            {itemsToDisplay.length > 0 && (
                <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={handlePageChange} loading={loading} />
            )}
        </div>
    );
};